import {User} from "../../api/accessControl/User.ts";
import {useEffect, useMemo, useState} from "react";
import {BackendApi} from "../../api/api.ts";
import LoadingFrame from "../../components/LoadingFrame.tsx";
import {Header} from "../../components/general/Header.tsx";
import Popup from "../../components/general/Popup.tsx";
import {InfoIcon} from "lucide-react";
import {BusinessSwitch} from "../../components/specific/BusinessSwitch.tsx";
import {InstitutionOption, InstitutionOptionList} from "./InstitutionOptionList.tsx";
import {initiateFinancialServiceConnection} from "./connectFinancialService.ts";
import {getConnectOptionDisplayName, CONNECTION_METHOD_FINANCIAL_SERVICE} from "../../lib/institutionDisplayNames.ts";

async function initiateConnectionWithBank(api: BackendApi, bank: InstitutionOption): Promise<void> {
    const data = await api.InitiateBankConnection({
        bank_id: bank.id,
        bank_account_id: null,
    });
    window.location.replace(data.redirect_url);
}

function matchesSearch(option: InstitutionOption, search: string) {
    if (!search) {
        return true
    }
    return getConnectOptionDisplayName(option).toLowerCase().startsWith(search.toLowerCase())
}

function filterOptions(options: InstitutionOption[], searchTerm: string) {
    if (!searchTerm) {
        return options
    }
    return options.filter((option) => matchesSearch(option, searchTerm))
}

export function ConnectABankPage({currentUser}: { currentUser: User }) {
    const backendApi = useMemo(
        () => new BackendApi(currentUser.getToken()),
        [currentUser],
    );

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isBankConnectionPopupOpen, setIsBankConnectionPopupOpen] = useState<InstitutionOption | false>(false);
    const [isFinancialServicePopupOpen, setIsFinancialServicePopupOpen] = useState<InstitutionOption | false>(false);

    async function connectFinancialService(option: InstitutionOption) {
        setIsLoading(true)
        try {
            await initiateFinancialServiceConnection(backendApi, option.id)
        } catch (error) {
            setIsLoading(false)
            alert(
                'Er is een fout opgetreden bij het starten van de koppeling. Probeer het later opnieuw.',
            )
            console.error('Error connecting financial service:', error)
        }
    }

    return (
        <LoadingFrame isLoading={isLoading}>
            <Popup open={!!isBankConnectionPopupOpen} title={'Koppelen via onze partner GoCardless'}
                   onClose={() => setIsBankConnectionPopupOpen(false)}
                   onConfirm={async () => {
                       if (!isBankConnectionPopupOpen) {
                           return
                       }
                       setIsBankConnectionPopupOpen(false)
                       setIsLoading(true)
                       try {
                           await initiateConnectionWithBank(backendApi, isBankConnectionPopupOpen)
                       } catch (error) {
                           setIsLoading(false)
                           alert(
                               'Er is een fout opgetreden bij het starten van de koppeling. Probeer het later opnieuw.',
                           )
                           console.error('Error initiating bank connection:', error)
                       }
                   }} Icon={InfoIcon} confirmButtonDisplayLabel={'Verder'} disableCancelButton={true}>
                <p>Voor je analyses en meldingen koppelen we je bankrekening veilig via onze
                    partner <strong>GoCardless</strong>.</p><br/>
                <p>Zij zorgen voor een soepel, goed beveiligde verbinding conform de PSD2 wetgeving. Je deelt alleen transacties
                    en saldi.</p><br/>
                <p>Klik op &apos;Verder&apos;. Je gaat dan heel even naar de beveiligde omgeving van GoCardless om de koppeling te
                    maken en komt daarna direct terug naar onze app.</p>
            </Popup>
            <Popup
                open={!!isFinancialServicePopupOpen}
                title={isFinancialServicePopupOpen ? `Koppelen met ${getConnectOptionDisplayName(isFinancialServicePopupOpen)}` : 'Koppelen'}
                onClose={() => setIsFinancialServicePopupOpen(false)}
                onConfirm={() => {
                    if (isFinancialServicePopupOpen) {
                        setIsFinancialServicePopupOpen(false)
                        connectFinancialService(isFinancialServicePopupOpen)
                    }
                }}
                Icon={InfoIcon}
                confirmButtonDisplayLabel={'Verder'}
                disableCancelButton={true}
            >
                {isFinancialServicePopupOpen && (
                    <>
                        <p>
                            We koppelen je <strong>{getConnectOptionDisplayName(isFinancialServicePopupOpen)}</strong>-account om saldi en
                            transacties op te halen voor je financiële analyses.
                        </p><br/>
                        <p>
                            Je deelt alleen gegevens die nodig zijn voor saldi en transacties. Klik op
                            &apos;Verder&apos; om de beveiligde koppeling te starten. Na het autoriseren kom je terug
                            naar onze app.
                        </p>
                    </>
                )}
            </Popup>
            <div>
                <Header/>
                <div className="bg-white">
                    <div className="mx-auto max-w-1xl px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
                        <h1 className="font-semibold text-3xl mb-6 ">Selecteer je zakelijke bank</h1>
                        <p className="mt-2 text-sm leading-6 text-gray-500 mb-8">
                            Kies alsjeblieft de bankrekening van je bedrijf, niet je persoonlijke rekening. Wij koppelen
                            met je bank om gegevens op te halen voor je financiële analyses.
                        </p>

                        <div className='flex items-center gap-2 my-6'>
                            <h2 className=" text-xl ">Rekening koppelen voor: </h2>
                            <BusinessSwitch currentUser={currentUser} className=""/>
                        </div>

                        <Tabs
                            api={backendApi}
                            onInstitutionSelected={(option: InstitutionOption) => {
                                if (option.connection_method === CONNECTION_METHOD_FINANCIAL_SERVICE) {
                                    setIsFinancialServicePopupOpen(option)
                                    return
                                }
                                setIsBankConnectionPopupOpen(option)
                            }}
                        />
                    </div>
                </div>
            </div>
        </LoadingFrame>
    )
}

function classNames(...classes: (string | boolean | undefined)[]) {
    return classes.filter(Boolean).join(' ')
}

type TabsProps = {
    api: BackendApi
    onInstitutionSelected: (option: InstitutionOption) => void
}

function Tabs({api, onInstitutionSelected}: TabsProps) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const tabs = [
        {name: '🇳🇱 Netherlands', countryCode: 'nl'},
        {name: '🇩🇪 Germany', countryCode: 'de'},
        {name: '🇧🇪 Belgium', countryCode: 'be'},
        {name: '🇬🇧 United Kingdom', countryCode: 'gb'},
    ];

    const [currentTab, setCurrentTab] = useState(tabs[0]);
    const [allBanks, setAllBanks] = useState<InstitutionOption[]>([]);
    const [allPaymentProviders, setAllPaymentProviders] = useState<InstitutionOption[]>([]);
    const [filteredBanks, setFilteredBanks] = useState<InstitutionOption[]>([]);
    const [filteredPaymentProviders, setFilteredPaymentProviders] = useState<InstitutionOption[]>([]);
    const [search, setSearch] = useState("");

    function applyFilters(banks: InstitutionOption[], paymentProviders: InstitutionOption[], searchTerm: string) {
        setFilteredBanks(filterOptions(banks, searchTerm))
        setFilteredPaymentProviders(filterOptions(paymentProviders, searchTerm))
    }

    useEffect(() => {
        let cancelled = false;

        setIsLoading(true)
        setFilteredBanks([])
        setFilteredPaymentProviders([])
        setAllBanks([])
        setAllPaymentProviders([])

        api.GetBanksByCountryCode(currentTab.countryCode)
            .then((data) => {
                if (cancelled) {
                    return
                }
                setAllBanks(data.banks)
                setAllPaymentProviders(data.payment_service_providers)
                applyFilters(data.banks, data.payment_service_providers, search)
            })
            .catch((error) => {
                if (!cancelled) {
                    alert(
                        'Er is een fout opgetreden bij het laden van banken. Probeer het later opnieuw.',
                    )
                    console.error('Error loading banks by country:', error)
                    throw error
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [currentTab.countryCode]);

    useEffect(() => {
        if (isLoading) {
            return
        }
        applyFilters(allBanks, allPaymentProviders, search)
    }, [search, isLoading, allBanks, allPaymentProviders]);

    return (
        <div>
            <div className="sm:hidden">
                <label htmlFor="tabs" className="sr-only">
                    Select a tab
                </label>
                <select
                    id="tabs"
                    name="tabs"
                    className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    defaultValue={tabs.find((tab) => currentTab.name === tab.name)?.name}
                    onChange={(e) => setCurrentTab(tabs.find((tab) => e.target.value === tab.name) ?? tabs[0])}
                >
                    {tabs.map((tab) => (
                        <option key={tab.name}>{tab.name}</option>
                    ))}
                </select>
            </div>
            <div className="hidden sm:block">
                <div className="border-b border-gray-200 flex justify-between items-center">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.name}
                                onClick={() => setCurrentTab(tab)}
                                className={classNames(
                                    currentTab.name === tab.name
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                    'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium'
                                )}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                    <div className="md:flex h-8 hidden items-center gap-2">
                        <input type="text" onChange={(e) => setSearch(e.target.value)}
                               className=" px-2 block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"/>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                             className="size-6">
                            <path fillRule="evenodd"
                                  d="M3.792 2.938A49.069 49.069 0 0 1 12 2.25c2.797 0 5.54.236 8.209.688a1.857 1.857 0 0 1 1.541 1.836v1.044a3 3 0 0 1-.879 2.121l-6.182 6.182a1.5 1.5 0 0 0-.439 1.061v2.927a3 3 0 0 1-1.658 2.684l-1.757.878A.75.75 0 0 1 9.75 21v-5.818a1.5 1.5 0 0 0-.44-1.06L3.13 7.938a3 3 0 0 1-.879-2.121V4.774c0-.897.64-1.683 1.542-1.836Z"
                                  clipRule="evenodd"/>
                        </svg>
                    </div>
                </div>
            </div>
            <div className="flex h-8 md:hidden mt-8 items-center gap-2">
                <input type="text" onChange={(e) => setSearch(e.target.value)}
                       className=" px-2 block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"/>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                    <path fillRule="evenodd"
                          d="M3.792 2.938A49.069 49.069 0 0 1 12 2.25c2.797 0 5.54.236 8.209.688a1.857 1.857 0 0 1 1.541 1.836v1.044a3 3 0 0 1-.879 2.121l-6.182 6.182a1.5 1.5 0 0 0-.439 1.061v2.927a3 3 0 0 1-1.658 2.684l-1.757.878A.75.75 0 0 1 9.75 21v-5.818a1.5 1.5 0 0 0-.44-1.06L3.13 7.938a3 3 0 0 1-.879-2.121V4.774c0-.897.64-1.683 1.542-1.836Z"
                          clipRule="evenodd"/>
                </svg>
            </div>
            <LoadingFrame isLoading={isLoading}>
                <InstitutionOptionList
                    items={filteredBanks}
                    isItemDisabled={(option) => option.is_connected === true}
                    disabledFeedback="Al gekoppeld voor dit bedrijf"
                    onItemClick={onInstitutionSelected}
                />
                {filteredPaymentProviders.length > 0 && (
                    <div className="mt-10">
                        <h2 className="text-xl font-semibold">Betaaldienstverleners</h2>
                        <p className="text-sm text-gray-500 mt-2 mb-2">
                            Koppel je betaalprovider om saldi en transacties op te halen.
                        </p>
                        <InstitutionOptionList
                            items={filteredPaymentProviders}
                            isItemDisabled={(option) => option.is_connected === true}
                            disabledFeedback="Al gekoppeld voor dit bedrijf"
                            onItemClick={onInstitutionSelected}
                        />
                    </div>
                )}
            </LoadingFrame>
        </div>
    )
}
